<script setup>
/* global axios */
// Servidores MCP INTERNOS da conta: o ByChat hospeda o endpoint e expõe as
// capacidades marcadas. Listagem self-contained (axios direto), visual nativo
// de Settings. Ao criar, abre a conexão (URL + token) na hora.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useBranding } from 'shared/composables/useBranding';
import { picoSearch } from '@chatwoot/pico-search';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import McpServerDialog from './McpServerDialog.vue';
import McpServerDetails from './McpServerDetails.vue';

const { t } = useI18n();
const { replaceInstallationName } = useBranding();
const route = useRoute();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/mcp/servers`;

const servers = ref([]);
const loading = ref(true);
const searchQuery = ref('');

const showDialog = ref(false);
const editingServer = ref(null);

const showDetails = ref(false);
const detailsServer = ref(null);

const showDeleteModal = ref(false);
const serverToDelete = ref(null);

const filteredServers = computed(() => {
  const q = searchQuery.value.trim();
  if (!q) return servers.value;
  return picoSearch(servers.value, q, ['name', 'description']);
});

const emptyListMessage = computed(() =>
  replaceInstallationName(t('MCP_MGMT.LIST.404'))
);
const pageDescription = computed(() =>
  replaceInstallationName(t('MCP_MGMT.DESCRIPTION'))
);

const tableHeaders = computed(() => [
  t('MCP_MGMT.LIST.TABLE_HEADER.NAME'),
  t('MCP_MGMT.LIST.TABLE_HEADER.CAPABILITIES'),
  t('MCP_MGMT.LIST.TABLE_HEADER.TOOLS'),
  t('MCP_MGMT.LIST.TABLE_HEADER.STATUS'),
  t('MCP_MGMT.LIST.TABLE_HEADER.ACTIONS'),
]);

const statusMeta = server =>
  server.enabled
    ? { color: '#22c55e', key: 'ACTIVE' }
    : { color: '#94a3b8', key: 'DISABLED' };

const toolsLabel = server => {
  const n = server.tools_count || 0;
  if (!n) return t('MCP_MGMT.TOOLS_NONE');
  if (n === 1) return t('MCP_MGMT.TOOLS_COUNT_ONE');
  return t('MCP_MGMT.TOOLS_COUNT', { n });
};

const fetchServers = async () => {
  loading.value = true;
  try {
    const { data } = await axios.get(base);
    servers.value = data.data || [];
  } catch (e) {
    servers.value = [];
  } finally {
    loading.value = false;
  }
};

const openAdd = () => {
  editingServer.value = null;
  showDialog.value = true;
};
const openEdit = server => {
  editingServer.value = server;
  showDialog.value = true;
};
const onSaved = ({ server, isNew }) => {
  showDialog.value = false;
  fetchServers();
  // ao criar, mostra a conexão (URL + token) na hora
  if (isNew && server) {
    detailsServer.value = server;
    showDetails.value = true;
  }
};

const openDetails = server => {
  detailsServer.value = server;
  showDetails.value = true;
};

const openDeleteModal = server => {
  serverToDelete.value = server;
  showDeleteModal.value = true;
};
const confirmDelete = async () => {
  const server = serverToDelete.value;
  showDeleteModal.value = false;
  try {
    await axios.delete(`${base}/${server.id}`);
    servers.value = servers.value.filter(s => s.id !== server.id);
    useAlert(t('MCP_MGMT.DELETE.SUCCESS'));
  } catch (e) {
    useAlert(t('MCP_MGMT.DELETE.ERROR'));
  }
};

onMounted(fetchServers);
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="t('MCP_MGMT.LOADING')"
    :no-records-found="!servers.length"
    :no-records-message="emptyListMessage"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="t('MCP_MGMT.HEADER')"
        :description="pageDescription"
        :search-placeholder="t('MCP_MGMT.SEARCH_PLACEHOLDER')"
      >
        <template v-if="servers.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ t('MCP_MGMT.COUNT', { n: servers.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="t('MCP_MGMT.ADD_BUTTON')"
            icon="i-lucide-plus"
            size="sm"
            @click="openAdd"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredServers"
        :no-data-message="t('MCP_MGMT.LIST.NO_RESULTS')"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="server in items" :key="server.id" :item="server">
            <template #default>
              <BaseTableCell>
                <div class="flex items-center gap-2 min-w-0">
                  <span
                    class="shrink-0 rounded-full size-2"
                    :style="{ background: statusMeta(server).color }"
                  />
                  <div class="flex flex-col min-w-0">
                    <span class="text-body-main text-n-slate-12 truncate">
                      {{ server.name }}
                    </span>
                    <span
                      class="text-xs truncate text-n-slate-10"
                      :title="server.endpoint_url"
                    >
                      {{ server.endpoint_url }}
                    </span>
                  </div>
                </div>
              </BaseTableCell>

              <BaseTableCell>
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="label in server.capability_labels"
                    :key="label"
                    class="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-11"
                  >
                    {{ label }}
                  </span>
                </div>
              </BaseTableCell>

              <BaseTableCell>
                <button
                  class="inline-flex items-center gap-1 text-body-main text-n-brand hover:underline"
                  @click="openDetails(server)"
                >
                  <span class="i-lucide-wrench size-3.5" />
                  {{ toolsLabel(server) }}
                </button>
              </BaseTableCell>

              <BaseTableCell>
                <span
                  class="text-body-main"
                  :style="{ color: statusMeta(server).color }"
                >
                  {{ t(`MCP_MGMT.STATUS.${statusMeta(server).key}`) }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="end">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <Button
                    v-tooltip.top="t('MCP_MGMT.ACTIONS.CONNECT')"
                    icon="i-lucide-plug-zap"
                    slate
                    sm
                    @click="openDetails(server)"
                  />
                  <Button
                    v-tooltip.top="t('MCP_MGMT.ACTIONS.EDIT')"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    @click="openEdit(server)"
                  />
                  <Button
                    v-tooltip.top="t('MCP_MGMT.ACTIONS.DELETE')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    @click="openDeleteModal(server)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal
      v-model:show="showDialog"
      :on-close="() => (showDialog = false)"
    >
      <McpServerDialog
        :server="editingServer"
        @saved="onSaved"
        @close="showDialog = false"
      />
    </woot-modal>

    <woot-modal
      v-model:show="showDetails"
      :on-close="() => (showDetails = false)"
    >
      <McpServerDetails
        v-if="detailsServer"
        :server="detailsServer"
        @close="showDetails = false"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
      :on-confirm="confirmDelete"
      :title="t('MCP_MGMT.DELETE.CONFIRM.TITLE')"
      :message="t('MCP_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="` ${serverToDelete?.name}?`"
      :confirm-text="t('MCP_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="t('MCP_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
